import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  useTheme,
  alpha,
  Skeleton
} from '@mui/material';
import { AccessTime, TrendingUp } from '@mui/icons-material';
import SessionBar from './SessionBar';
import SessionDetailModal from './SessionDetailModal';
import { SessionTimeline as SessionTimelineType, formatCurrency, getSessionDetail } from '../../api/invoiceApi';

interface SessionTimelineProps {
  sessions: SessionTimelineType[];
  loading?: boolean;
  /** Start of the visible time range (for timeline positioning) */
  rangeStart?: Date;
  /** End of the visible time range (for timeline positioning) */
  rangeEnd?: Date;
  /** Display mode: 'list' for vertical list, 'timeline' for horizontal bars */
  displayMode?: 'list' | 'timeline';
  /** Callback when a session is clicked */
  onSessionClick?: (sessionId: number) => void;
}

/**
 * SessionTimeline - Displays a timeline of sessions for a table.
 * Supports list view and visual timeline view with time-positioned bars.
 */
const SessionTimeline: React.FC<SessionTimelineProps> = ({
  sessions,
  loading = false,
  rangeStart,
  rangeEnd,
  displayMode = 'list',
  onSessionClick
}) => {
  const theme = useTheme();
  const [selectedSession, setSelectedSession] = useState<SessionTimelineType | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Calculate total revenue
  const totalRevenue = sessions.reduce((sum, s) => sum + s.totalAmount, 0);
  const activeCount = sessions.filter(s => s.endedAt === null).length;

  // Calculate time range for positioning
  const timeRange = React.useMemo(() => {
    if (rangeStart && rangeEnd) {
      return { start: rangeStart.getTime(), end: rangeEnd.getTime() };
    }
    // Auto-calculate from sessions
    if (sessions.length === 0) {
      const now = new Date();
      return { start: now.setHours(0, 0, 0, 0), end: Date.now() };
    }
    const times = sessions.flatMap(s => [
      new Date(s.startedAt).getTime(),
      s.endedAt ? new Date(s.endedAt).getTime() : Date.now()
    ]);
    return {
      start: Math.min(...times),
      end: Math.max(...times)
    };
  }, [sessions, rangeStart, rangeEnd]);

  // Calculate position percentages for timeline view
  const getBarPosition = (session: SessionTimelineType) => {
    const startTime = new Date(session.startedAt).getTime();
    const endTime = session.endedAt ? new Date(session.endedAt).getTime() : Date.now();
    const totalDuration = timeRange.end - timeRange.start;

    if (totalDuration <= 0) return { left: 0, width: 100 };

    const left = ((startTime - timeRange.start) / totalDuration) * 100;
    const width = ((endTime - startTime) / totalDuration) * 100;

    return {
      left: Math.max(0, Math.min(100, left)),
      width: Math.max(2, Math.min(100 - left, width))
    };
  };

  const handleSessionClick = async (sessionId: number) => {
    // Find the session in the list first
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      setSelectedSession(session);
      setModalOpen(true);
      return;
    }

    // If not found, fetch from API
    setLoadingDetail(true);
    setModalOpen(true);
    try {
      const detail = await getSessionDetail(sessionId);
      setSelectedSession(detail);
    } catch (err) {
      console.error('Failed to fetch session detail:', err);
    } finally {
      setLoadingDetail(false);
    }

    onSessionClick?.(sessionId);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedSession(null);
  };

  // Generate time labels for timeline view
  const getTimeLabels = () => {
    const labels = [];
    const duration = timeRange.end - timeRange.start;
    const hourMs = 3600000;

    // Determine label interval based on duration
    let interval = hourMs; // 1 hour default
    if (duration > hourMs * 12) interval = hourMs * 2;
    if (duration > hourMs * 24) interval = hourMs * 4;

    const startHour = new Date(timeRange.start);
    startHour.setMinutes(0, 0, 0);

    for (let t = startHour.getTime(); t <= timeRange.end; t += interval) {
      if (t >= timeRange.start) {
        const percent = ((t - timeRange.start) / duration) * 100;
        labels.push({
          time: new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          percent
        });
      }
    }

    return labels;
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={60}
            sx={{ mb: 1 }}
          />
        ))}
      </Box>
    );
  }

  if (sessions.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No sessions in this time range
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary Stats */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          mb: 2,
          p: 1.5,
          backgroundColor: alpha(theme.palette.primary.main, 0.03),
          borderRadius: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2">
            {sessions.length} session{sessions.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        {activeCount > 0 && (
          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
            {activeCount} active
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
          <TrendingUp sx={{ fontSize: 18, color: 'success.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
            {formatCurrency(totalRevenue)}
          </Typography>
        </Box>
      </Box>

      {/* Timeline View */}
      {displayMode === 'timeline' ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 1.5,
            position: 'relative',
            minHeight: 80
          }}
        >
          {/* Time Labels */}
          <Box sx={{ position: 'relative', height: 20, mb: 1 }}>
            {getTimeLabels().map((label, i) => (
              <Typography
                key={i}
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  left: `${label.percent}%`,
                  transform: 'translateX(-50%)',
                  fontSize: '0.65rem'
                }}
              >
                {label.time}
              </Typography>
            ))}
          </Box>

          {/* Timeline Track */}
          <Box
            sx={{
              position: 'relative',
              height: 32,
              backgroundColor: alpha(theme.palette.divider, 0.3),
              borderRadius: 0.5
            }}
          >
            {sessions.map((session) => {
              const pos = getBarPosition(session);
              return (
                <SessionBar
                  key={session.id}
                  session={session}
                  onClick={handleSessionClick}
                  leftPercent={pos.left}
                  widthPercent={pos.width}
                  compact
                />
              );
            })}
          </Box>
        </Paper>
      ) : (
        /* List View */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {sessions.map((session) => (
            <SessionBar
              key={session.id}
              session={session}
              onClick={handleSessionClick}
            />
          ))}
        </Box>
      )}

      {/* Session Detail Modal */}
      <SessionDetailModal
        open={modalOpen}
        onClose={handleModalClose}
        session={selectedSession}
        loading={loadingDetail}
      />
    </Box>
  );
};

// Skeleton for loading state
export const SessionTimelineSkeleton: React.FC = () => {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Skeleton variant="text" width={100} />
        <Skeleton variant="text" width={80} />
      </Box>
      {[1, 2, 3].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={56}
          sx={{ mb: 1 }}
        />
      ))}
    </Box>
  );
};

export default SessionTimeline;
